/**
 * Game configuration & tuning constants for Blood Moon.
 * These are DATA, not hardcoded logic — the engine reads them so future
 * games/skins can override (plan Gameplay tweak #4/#7).
 */
export const MIN_PLAYERS = 5;
export const MAX_PLAYERS = 10;
/**
 * Role distribution by player count.
 * Wolves: 5–6 → 1 · 7–9 → 2 · 10 → 3. Exactly 1 Seer. Everyone else Villager.
 * Returns a count per role; the sum always equals `playerCount`.
 * Throws for counts outside [MIN_PLAYERS, MAX_PLAYERS].
 */
export function roleDistribution(playerCount) {
    if (playerCount < MIN_PLAYERS || playerCount > MAX_PLAYERS) {
        throw new Error(`roleDistribution: playerCount ${playerCount} out of range [${MIN_PLAYERS}, ${MAX_PLAYERS}]`);
    }
    const werewolves = wolfCount(playerCount);
    const seers = 1;
    const villagers = playerCount - werewolves - seers;
    // Roles belonging to the other games are never dealt by Blood Moon — always 0.
    // They appear here only so the map stays total over the (now 7-role) `RoleId`
    // union; buildRolePool pushes nothing for a 0 count, so Blood Moon's role pool
    // is unchanged. (THIEF/JOKER are Blood Moon: One Night's; that variant deals
    // its own pool via `oneNightDistribution`, never through this function.)
    return {
        WEREWOLF: werewolves,
        SEER: seers,
        VILLAGER: villagers,
        DECEIVER: 0,
        PALADIN: 0,
        THIEF: 0,
        JOKER: 0,
    };
}
/** Wolf count for a table size (5–6 → 1, 7–9 → 2, 10 → 3). */
export function wolfCount(playerCount) {
    if (playerCount <= 6)
        return 1;
    if (playerCount <= 9)
        return 2;
    return 3;
}
/**
 * Soft timers (seconds) for each timed phase + the discussion bounds.
 * ADVANCE (timer expiry OR host skip) applies the deterministic fallbacks.
 */
export const TIMERS = {
    /** Role reveal — all-ready or auto-advance (stragglers can't stall the game). */
    DEAL: 60,
    /** Nightfall pause: everyone sleeps + ambient music before the wolves wake. */
    NIGHT_INTRO: 10,
    /** Wolf pick — no submission → random living non-wolf. */
    NIGHT_WOLVES: 40,
    /**
     * Interlude pause: the wolves close their eyes (WOLVES_SLEEP voice + stinger)
     * before the seer is summoned. Long enough for the go-to-sleep line to finish
     * so it reads as its own beat, distinct from the seer waking.
     */
    NIGHT_INTERLUDE: 8,
    /** Seer pick — no submission → skip (no info). */
    NIGHT_SEER: 30,
    /**
     * After the seer confirms a real investigation, the night HOLDS on NIGHT_SEER
     * for this long so the seer can calmly read their vision before dawn breaks.
     * The result resolves immediately (private), but the phase only advances when
     * this short timer fires. A skip (no vision) advances instantly instead.
     */
    SEER_REVEAL_HOLD: 5,
    /** Dawn reveal — auto-advances to discussion (host can skip early). */
    DAWN: 8,
    /** Discussion default (host-adjustable within [MIN, MAX]). */
    DISCUSS_DEFAULT: 180,
    DISCUSS_MIN: 60,
    DISCUSS_MAX: 300,
    /** Vote — non-voters counted as Abstain. */
    VOTE: 30,
    /**
     * Lynch reveal (tie / no-lynch) — auto-advances (runs win check → END or next
     * night). The scoreboard (per-voter tally) lifts ~2.5s in, so ~13s leaves it
     * on screen ~10s — enough to read who voted whom without dragging. Host can
     * skip early.
     */
    RESOLVE_VOTE: 13,
    /**
     * Lynch reveal when someone actually hangs — the staged suspense (last words →
     * name → role) runs first and the tally lifts ~8s in, so ~18s keeps the
     * scoreboard up ~10s. Host can skip early.
     */
    RESOLVE_VOTE_LYNCH: 18,
};
/**
 * Mid-phase narrator countdown warnings, seconds before the phase deadline.
 * The SERVER schedules these alongside the phase timer (paused with it) and
 * voices a TIME_WARNING beat when they fire. Phases absent here get none.
 */
export const TIME_WARNINGS = {
    DEAL: 10,
    DISCUSS: 30,
};
/**
 * Room codes: unambiguous alphabet — no I, O, 0, or 1 (avoids read-aloud
 * confusion). 4-letter codes minted by the server, collision-checked.
 */
export const ROOM_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
export const ROOM_CODE_LENGTH = 4;
/** Locked config flags (flags, not hardcodes — see plan Gameplay tweak #4). */
export const RULES = {
    /** Dead players' roles are always revealed. */
    revealRoleOnDeath: true,
    /** First night includes a kill (keeps games short). */
    firstNightKill: true,
    /** Tie vote → no elimination this round (MVP, no revote). */
    tiePolicy: 'no_lynch',
    /** Voting allows Abstain. */
    allowAbstain: true,
    /** Vote mode: highest count wins (plurality), not majority. */
    voteMode: 'plurality',
};
/** Store / lobby presentation metadata for Blood Moon. */
export const GAME_META = {
    id: 'bloodmoon',
    name: 'Blood Moon',
    subtitle: 'Werewolf game with a dangerous twist',
    players: '5–10',
    time: '~20 min',
    theme: 'Ravenhollow',
};
export const DEFAULT_CONFIG = {
    discussionSecs: TIMERS.DISCUSS_DEFAULT,
};
/** Clamp a requested discussion length into the allowed band. */
export function clampDiscussionSecs(secs) {
    return Math.max(TIMERS.DISCUSS_MIN, Math.min(TIMERS.DISCUSS_MAX, Math.round(secs)));
}
/**
 * Blood Moon win check. Village wins when wolves == 0; wolves win at parity
 * (wolves ≥ non-wolves). Returns the winning team or null to continue.
 */
export function bloodMoonWinCheck(counts) {
    if (counts.wolves === 0)
        return 'VILLAGE';
    if (counts.wolves >= counts.nonWolves)
        return 'WOLVES';
    return null;
}
//# sourceMappingURL=config.js.map